
import { ComponentProps } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { getNodeAtPath, Mosaic, MosaicBranch, MosaicDragItem, MosaicDragType, MosaicNode, updateTree } from '@/shared/components/Mosaic'
import { MosaicDropTargetPosition } from '@/shared/components/Mosaic/internalTypes'
import { getNumberOfLeaves, isKeyExistedInTree } from '@/shared/components/Mosaic/lib/mosaicUtilities'
import { WidgetType, WidgetTypeWithSuffix } from '@/shared/components/Mosaic/models/widgetType'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { MAX_WINDOW_COUNT, MOSAIC_LAYOUT_ID } from '@/shared/config/consts'
import { useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { styled } from '@mui/material'

import { WidgetElementType } from './model/AdditionalControlsType'
import { MosaicWindowWidget } from './MosaicWindowWidget'
import { MosaicLayoutZeroState } from './MosalicLayoutZeroState'

export type WidgetElementMappingType = Record<WidgetType, WidgetElementType>

export type MosaicLayoutProps = ComponentProps<typeof MosaicLayoutContainer> & {
  widgetElementMapping: WidgetElementMappingType
  zeroStateProps: Omit<ComponentProps<typeof MosaicLayoutZeroState>, 'onNodeCreate'>
  currentNode: MosaicNode<WidgetTypeWithSuffix> | null
  setCurrentNode: (currentNode: MosaicNode<WidgetTypeWithSuffix> | null) => void
  maxWindows?: number
  slotProps?: {
    mosaicWindowToolbar: Pick<ComponentProps<typeof MosaicWindowWidget>, 'isAdditionalToolbarEnabled' | 'additionalToolbarElement'>
  }
}

export const MosaicLayout: React.FC<MosaicLayoutProps> = ({ widgetElementMapping, zeroStateProps, currentNode, setCurrentNode, maxWindows = MAX_WINDOW_COUNT, ...props }) => {
  const isLayoutLock = useAppSelector(state => state.widgetWindows.isLayoutLock)
  const { setToastContent } = useToastContext()


  const onNodeChange = (newNode: MosaicNode<WidgetTypeWithSuffix> | null) => {
    if (!newNode) return

    setCurrentNode(newNode)
  }

  // Insert a new node into the binary tree when a tool widget or tree widget is dropped onto the grid.
  const onDropCreate = (item: MosaicDragItem, position: MosaicDropTargetPosition, path: MosaicBranch[]) => {
    if (getNumberOfLeaves(currentNode) >= maxWindows) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Reached Window Limit',
        content: `You have reached the maximum limit of ${maxWindows} open windows. Please close one or more windows to continue.`,
      })

      return
    }

    const destination = getNodeAtPath(currentNode, path) as MosaicNode<WidgetTypeWithSuffix>
    const direction = position === 'top' || position === 'bottom' ? 'column' : 'row'

    let widgetKey: WidgetTypeWithSuffix = `${item.widgetType}-${uuidv4()}`

    // Widget is a tree item
    if (item.type === MosaicDragType.TREE_ITEM) {
      widgetKey = item.itemId

      // Check if the widget already exists in the grid
      const isWidgetExisted = isKeyExistedInTree(currentNode, widgetKey)

      if (isWidgetExisted) {
        setToastContent({
          open: true,
          severity: 'error',
          title: 'Window Already Exists',
          content: 'The window you are trying to add is already in the grid.',
        })

        return
      }
    }

    let first: MosaicNode<WidgetTypeWithSuffix>
    let second: MosaicNode<WidgetTypeWithSuffix>

    if (position === 'top' || position === 'left') {
      first = widgetKey
      second = destination
    } else {
      first = destination
      second = widgetKey
    }

    if (!currentNode) {
      // TODO: Handle empty node
      return
    }

    const updatedRootNode = updateTree(currentNode, [
      {
        path,
        spec: {
          $set: {
            direction,
            first,
            second,
          },
        },
      },
    ])

    setCurrentNode(updatedRootNode)
  }

  // Initialize the first zero state node
  const onZeroStateNodeCreate = (item: MosaicDragItem) => {
    let widgetKey: WidgetTypeWithSuffix = `${item.widgetType}-${uuidv4()}`

    if (item.type === MosaicDragType.TREE_ITEM) {
      widgetKey = item.itemId
    }

    setCurrentNode(widgetKey)
  }

  // Render a widget window tile
  const onRenderTile = (widgetId: WidgetTypeWithSuffix, path: MosaicBranch[]) => {
    const [widgetType, id] = widgetId.split('-') as [WidgetType, string]

    const componentProps = { id: id, widgetId: widgetId }
    const {
      widgetElement: WidgetComponent,
      isAdditionalToolbarEnabled,
      additionalToolbarElement,
      toolbarActions,
      CustomWindowContext,
    } = widgetElementMapping[widgetType]

    return (
      <MosaicWindowWidget
        widgetId={widgetId}
        widgetType={widgetType}
        onDropCreate={onDropCreate}
        draggable={!isLayoutLock}
        path={path}
        isAdditionalToolbarEnabled={isAdditionalToolbarEnabled}
        additionalToolbarElement={additionalToolbarElement}
        toolbarActions={toolbarActions}
        CustomWindowContext={CustomWindowContext}
      >
        <WidgetComponent {...componentProps} />
      </MosaicWindowWidget>
    )
  }

  return (
    <MosaicLayoutContainer {...props} >
      <Mosaic<WidgetTypeWithSuffix>
        mosaicId={MOSAIC_LAYOUT_ID}
        renderTile={onRenderTile}
        value={currentNode}
        zeroStateView={
          <MosaicLayoutZeroState
            onNodeCreate={onZeroStateNodeCreate}
            {...zeroStateProps}
          />
        }
        onChange={onNodeChange}
        onDropCreate={onDropCreate}
        resize={isLayoutLock ? 'DISABLED' : {
          minimumPaneSizePercentage: 25,
        }}
      />
    </MosaicLayoutContainer>
  )
}

export const MosaicLayoutContainer = styled('div')`
  height: 100%;
  width: calc(100% - ${pxToRem(8)});
  margin: 0 ${pxToRem(9)} ${pxToRem(8)} 0;
`
